import React, { useCallback, useRef, useState } from 'react';
import {
	TermItem,
	useCsszSickLeaveRepairDataQuery,
	useLazyTermItemByIdQuery,
	useLazyTermItemByValueQuery,
} from '../../../../graphql/graphql';
import Loading from '../../../../Components/Loading';
import CSSZSickLeaveCertificateForm from '../CSSZSickLeaveCertificateCreate/CSSZSickLeaveCertificateForm';
import {
	CSSZSubmitValues,
	CSSZSickLeaveValues,
} from '../CSSZSickLeaveCertificateCreate/CSSZSickLeaveCertificateCreate';
import { CSSZUser } from '../../Interfaces/CSSZ.interface';
import { DeepPartial } from '../../../../Interfaces/DeepPartial';
import { useEffectAsync } from '../../../../helpers';
import { hasOutingsData } from '../../Validation/CSSZValidation';

interface Props {
	documentId: string;
	user: CSSZUser;
	onSubmit: (values: CSSZSickLeaveValues) => void;
}

const formatInsuranceAdministratorLabel = (termItem: TermItem) => {
	const value = termItem.value?.trim();
	const name = termItem.name?.trim();

	if (value && name) {
		return `${value} - ${name}`;
	}

	return name || value || termItem.id;
};

const CSSZSickLeaveCertificateRepair = ({
	documentId,
	user,
	onSubmit,
}: Props) => {
	const [formValues, setFormValues] =
		useState<DeepPartial<CSSZSickLeaveValues>>();
	const formValuesRef = useRef<DeepPartial<CSSZSickLeaveValues>>();
	const { data, isLoading } = useCsszSickLeaveRepairDataQuery({
		id: documentId,
	});
	const [getTermItemByValue] = useLazyTermItemByValueQuery();
	const [getTermItemById] = useLazyTermItemByIdQuery();

	useEffectAsync(async () => {
		const formData = await apiToFormData(data?.document?.item?.extra?.data);
		formValuesRef.current = formData;
		setFormValues(formData);
	}, [data]);

	const apiToFormData = async (
		apiValues: any,
	): Promise<DeepPartial<CSSZSickLeaveValues>> => {
		if (!apiValues) {
			return {};
		}

		const state = await getTermItemByValue({
			value: apiValues.patientAddress.state,
			termType: 'cssz_ciselnik_zemi',
		});

		const stateOption = state?.data?.termItem
			? {
					label: state.data.termItem.name,
					value: state.data.termItem.value,
			  }
			: undefined;

		const patientAddress = {
			...apiValues.patientAddress,
			state: stateOption,
		};

		const employmentState = apiValues.employment?.address?.state
			? await getTermItemByValue({
					value: apiValues.employment.address.state,
					termType: 'cssz_ciselnik_zemi',
			  })
			: null;

		const employmentStateOption = employmentState?.data?.termItem
			? {
					label: employmentState.data.termItem.name,
					value: employmentState.data.termItem.value,
			  }
			: undefined;

		const typeOfEmployment = apiValues.employment.typeOfEmployment
			? await getTermItemByValue({
					value: apiValues.employment.typeOfEmployment,
					termType: 'cssz_ciselnik_typ_zamestnani',
			  })
			: null;

		const typeOfEmploymentOption = typeOfEmployment?.data?.termItem
			? {
					label: typeOfEmployment.data.termItem.name,
					value: typeOfEmployment.data.termItem.value,
			  }
			: undefined;

		const employment = {
			...apiValues.employment,
			address: {
				...apiValues.employment.address,
				state: employmentStateOption,
			},
			typeOfEmployment: typeOfEmploymentOption,
		};

		const insuranceAdministrator = await getTermItemById({
			id: apiValues.sickLeave.insuranceAdministrator,
		});

		const insuranceAdministratorOption = insuranceAdministrator?.data?.termItem
			? {
					value: insuranceAdministrator.data.termItem.id,
					label: formatInsuranceAdministratorLabel(
						insuranceAdministrator.data.termItem,
					),
			  }
			: undefined;

		const {
			outings: apiOutings,
			diagnosis,
			insuranceAdministrator: _insuranceAdministratorId,
			...restSickLeave
		} = apiValues.sickLeave;

		const sickLeave = {
			...restSickLeave,
			...(diagnosis
				? { diagnosis: { value: diagnosis, label: diagnosis } }
				: {}),
			...(insuranceAdministratorOption
				? { insuranceAdministrator: insuranceAdministratorOption }
				: {}),
			...(hasOutingsData(apiOutings) ? { outings: apiOutings } : {}),
		};

		return {
			patientAddress,
			employment,
			sickLeave,
			decisionNumber: apiValues.decisionNumber,
		};
	};

	const handleSubmitValues = useCallback(
		(values: CSSZSubmitValues, send = false) => {
			const prev = formValuesRef.current;
			if (!prev) {
				return;
			}

			const next = { ...prev } as CSSZSickLeaveValues;

			if (values.type === 'sickLeave') {
				next.sickLeave = {
					...prev.sickLeave,
					...values.data,
				} as CSSZSickLeaveValues['sickLeave'];
			} else if (values.data !== undefined) {
				next[values.type] = values.data;
			}

			formValuesRef.current = next;
			setFormValues(next);

			if (send) {
				onSubmit(next);
			}
		},
		[onSubmit],
	);

	if (isLoading || !formValues) {
		return <Loading />;
	}

	return (
		<CSSZSickLeaveCertificateForm
			onSubmit={handleSubmitValues}
			user={user}
			collectedValues={formValues}
			showSubmitToCssz={false}
			resetOnCollectedValuesChange={false}
		/>
	);
};

export default CSSZSickLeaveCertificateRepair;
